import { isSameApi } from '../api/api-url';
import type { HttpClient, RequestOptions } from '../api/http';
import { HttpFailure } from '../api/http-failure';
import { appConfig } from '../config/app-config';
import { account, currentSessionEpoch } from '../session/session.store';
import { clock } from '../time/clock';
import { BUILD_ID } from '../../../environments/build-id';
import { advanceResync } from '../reach/resync.store';
import { isAllowedStoredViewUrl } from './allow-list';
import { readStoredView, writeStoredView } from './device-views';
import { clearShownFromStorage, markShownFromStorage } from './presentation.store';

/**
 * Writes for one URL, one after another. Each entry is the tail of that URL's queue; a write that
 * fails is logged and does not stop the next. The device would not keep that answer, so only the
 * fallback for next time is missing, which is no worse than never having reached the screen.
 */
const writeQueues = new Map<string, Promise<void>>();

function queueWrite(url: string, write: () => Promise<void>): void {
  const tail = (writeQueues.get(url) ?? Promise.resolve())
    .then(write)
    .catch((error: unknown) => {
      console.warn('stored-views: could not write the device', error);
    });
  writeQueues.set(url, tail);
  void tail.then(() => {
    if (writeQueues.get(url) === tail) writeQueues.delete(url);
  });
}

/**
 * 036 (issue 208, research R-008) — the storing client. Composed
 * `storing(observing(renewing(…)))` in `main.tsx`, inside the offline guard: OUTSIDE renewing, so a
 * request this wraps already carries its token and has had its one retry, and OUTSIDE observing,
 * so `core/reach/` sees the raw outcome of the request. Were it inside, a fallback to a stored view
 * would read as a success, and the connection line would never appear.
 *
 * Only `GET` is ever touched — every other verb passes straight through, untouched, because nothing
 * this cycle stores or replays a write through this seam (that is the outbox's own door).
 *
 * A GET off the allow-list, or made before any practitioner is known (there is no database to name
 * yet), is passed straight through too: nothing here has an opinion about it either way.
 */

/**
 * 036 (issue 234) — a readable GET's own deadline, covering everything `inner.get()` can be waiting
 * on, not only `http.ts`'s own `READ_TIMEOUT_MS` (which starts counting only once a request is
 * actually SENT). On a cold launch `renewing-client.ts` makes the first read wait for a renewal
 * already in flight, itself stalled until the session's own `SESSION_TIMEOUT_MS` (5s) — so without
 * a deadline here, a readable screen's fallback could not fire before roughly 5s + 3s, blowing
 * SC-002's 3.5s budget for a reason that has nothing to do with this file.
 *
 * **2.5 seconds, deliberately SHORTER than `READ_TIMEOUT_MS`'s 3s** (tuned 2026-10-01, the spec's own
 * "first values … expected to be tuned"). At 3s this deadline left SC-002's 3.5s budget only 0.5s to
 * start the service worker, read the device database and draw the stored list. The release CI runner
 * needed 0.51–0.6s and missed SC-002 on all four cold-launch tests (3510–3596ms, release PR 265,
 * run 466), and a mid-range phone is nearer that runner than the workstation. 2.5s leaves a full
 * second. The two numbers now mean different things: this one is when a screen stops WAITING and
 * shows what it has stored; `READ_TIMEOUT_MS` is when a read is ABANDONED and the server counted as
 * not answering (FR-004). An answer landing between 2.5s and 3s still replaces the stored view, and
 * `stored-as-of` stays up until it does.
 */
export const STORED_VIEW_DEADLINE_MS = 2_500;

/**
 * The deadline actually armed below — `STORED_VIEW_DEADLINE_MS` unless a test has overridden it.
 *
 * **Why a mutable binding rather than fake timers.** `storing-client.spec.ts`'s own deadline tests
 * need a REAL device database (`readStoredView`/`writeStoredView`, `fake-indexeddb`-backed), and
 * `vi.useFakeTimers()` deadlocks against it: `fake-indexeddb` schedules its own internal completion
 * work in a way fake timers do not drive, so a test that fakes time and then awaits a database call
 * inside this file's own deadline timer never resolves — measured, not assumed (every one of the
 * three deadline-crossing tests timed out at Vitest's own 5s ceiling before this seam existed). A
 * short REAL deadline sidesteps the conflict entirely rather than fighting two clocks against each
 * other, the same reasoning `setAppClient`/`setDirectClient` already use for a seam nothing but a
 * test may call.
 */
let deadlineMs = STORED_VIEW_DEADLINE_MS;

/** Test seam, paired with `resetStoredViewDeadlineMsForTest`. Production code never calls it. */
export function setStoredViewDeadlineMsForTest(ms: number): void {
  deadlineMs = ms;
}

/** Test seam. Production code never calls it. */
export function resetStoredViewDeadlineMsForTest(): void {
  deadlineMs = STORED_VIEW_DEADLINE_MS;
}

export function createStoringClient(inner: HttpClient): HttpClient {
  const get = async <T,>(url: string, options?: RequestOptions): Promise<T> => {
    if (!isSameApi(url, appConfig().apiBaseUrl) || !isAllowedStoredViewUrl(url)) {
      return inner.get<T>(url, options);
    }

    const practitionerId = account.get()?.practitionerId;
    if (practitionerId == null) return inner.get<T>(url, options);

    /**
     * 036 (issue 258, B2, FR-046) — the session epoch AT THE MOMENT THIS READ STARTED. `persist`
     * below re-checks this, fresh, at the moment its queued write actually RUNS — not here, and not
     * merely once when the answer arrives — because a write can sit behind others in `writeQueues`
     * for a while after that. `practitionerId` alone cannot catch this: the SAME practitioner signing
     * out and back in on the same device still bumps the epoch, and a write queued under the old one
     * is still not that new session's to finish just because the id happens to match again.
     */
    const epochAtRead = currentSessionEpoch();

    let date: string | null = null;
    /**
     * 036 (issue 234, found in review) — set the instant the DEADLINE below actually resolves this
     * call's own promise from storage, before `outcome`'s success handler ever runs. It is how that
     * handler tells a LATE live success (arriving after the screen was already given a stale answer)
     * from an on-time one, which need opposite treatment — see the handler's own comment.
     */
    let resolvedFromDeadline = false;
    const live = inner.get<T>(url, {
      ...options,
      // http.ts's one extension point (R-008): reads the server's `Date` header without a second
      // fetch. `null` is a real possibility this client tolerates — see the fallback below.
      onHeaders: (headers) => {
        date = headers.get('Date');
      },
    });

    /** The write every successful answer makes, on time or late alike — nothing about persisting the
     *  fresh body depends on which caller is still waiting for it. */
    const persist = (body: T): void => {
      // Not awaited: storing is for NEXT time. A write opens its own database connection, which on a
      // loaded device is the difference between a list drawn now and a list drawn late. Writes for
      // one URL are queued behind each other, so the answer that arrived last is the one left stored,
      // whichever write the device finishes first.
      queueWrite(url, () => {
        // 036 (issue 258, B2, FR-046) — a first, cheap check HERE, at the moment this queued write
        // actually runs rather than when the answer first arrived (`clear()` may have run, and
        // `clearStoredViewsFor` may already have finished, while this write was sitting behind an
        // earlier one for the same URL) — skips even opening the device connection for the common
        // case. Not the ONLY check: `writeStoredView`'s own `stillValid` parameter re-checks this
        // same comparison again, immediately before the write itself, which is what actually closes
        // the race this one only narrows — see that parameter's own comment for why the gap between
        // THIS check and the transaction `db.putView` creates (a real connection-open round trip,
        // `openDeviceDatabaseExclusive`'s own header says why) is exactly wide enough for `clear()`'s
        // own write to land in between.
        if (currentSessionEpoch() !== epochAtRead) return Promise.resolve();
        return writeStoredView(
          practitionerId,
          {
            url,
            body,
            // The server's own header is preferred; its absence (a development proxy that does not
            // pass it through, a body-less test double) still leaves something to show rather than
            // nothing, though FR-018 wants the SERVER'S statement wherever it is available.
            // `clock()`, never `new Date()` directly (core/time/no-second-reduction.spec.ts) — this
            // module is not core/time/, and "now" is one substitutable thing everywhere else too.
            date: date ?? clock().toUTCString(),
            buildId: BUILD_ID,
          },
          () => currentSessionEpoch() === epochAtRead,
        );
      });
    };

    /**
     * The ordinary outcome of the read, whenever it finally settles — on time, or (issue 234) long
     * after this function's own promise has already resolved from storage.
     */
    const outcome = live.then(
      (body): { readonly ok: true; readonly body: T } => {
        persist(body);
        if (resolvedFromDeadline) {
          // 036 (issue 234, found in review) — the screen was already handed the STORED body by the
          // deadline below; it is not looking at THIS one. Clearing the mark here, as the on-time
          // branch does, would make the "Showing your list as of…" line disappear while the visible
          // list is still the old one — presenting stale content as current, exactly what FR-022
          // forbids. The fresh body is persisted above for next time; `advanceResync()` asks every
          // mounted screen to re-read for real, and THAT ordinary read's own on-time success is what
          // actually clears the mark, once the screen is genuinely showing what it claims to.
          // `core/reach/reach.store.ts`'s own `noteOutcome` cannot be the trigger here: nothing
          // failed (the read was merely slow), so its state goes `unknown` → `reachable` — not a
          // transition FROM `unreachable`/`failing` — and its resync-on-transition rule never fires.
          // Measured: this is the defect's exact mechanism, found in review of the first version of
          // this file's deadline.
          advanceResync();
        } else {
          // A live, ON-TIME answer: this URL is no longer being shown from storage (FR-022), and the
          // fresh body REPLACES whatever was stored before (R-008's own "replaced, never merged"). The
          // screen that asked IS looking at this body — nothing stale is left mislabelled.
          clearShownFromStorage(url);
        }
        return { ok: true, body };
      },
      (error: unknown) => {
        if (!(error instanceof HttpFailure)) throw error;
        return { ok: false as const, error };
      },
    );

    /** Awaits `outcome`, applying the fallback-or-rethrow rule the read has always had. */
    const resolveOutcome = async (): Promise<T> => {
      const result = await outcome;
      if (result.ok) return result.body;
      const error = result.error;

      // "cannot presently be answered" — status 0 (unreachable, including a read past its time
      // limit) or any 5xx (failing). Every other refusal (401, 403, a genuine 4xx) is the server
      // considering the request and answering it; there is nothing to fall back from.
      const cannotAnswer = error.status === 0 || error.status >= 500;
      // `requireLiveAnswer` (research R-008 as amended, e2e/move-to-picker.spec.ts's "re-read
      // fails" case): a GET confirming a write must surface its own refusal — FR-010's existing
      // contract — never be masked by an answer that predates the write.
      if (!cannotAnswer || options?.requireLiveAnswer) throw error;

      const stored = await readStoredView(practitionerId, url).catch(() => null);
      // "A view stored by another build is treated as absent" (R-008) — never served, and never
      // reported as though nothing were there at all: the caller still learns the read failed.
      if (!stored || stored.buildId !== BUILD_ID) throw error;

      markShownFromStorage(url, { moment: stored.date, reason: error.status === 0 ? 'unreachable' : 'failing' });
      return stored.body as T;
    };

    // A GET confirming a write (`requireLiveAnswer`) must surface the read's OWN refusal — R-008's
    // rule, unchanged — so it is never raced against this deadline: `resolveOutcome()` already
    // skips the fallback for it above, and racing would only risk resolving with a stale body a
    // confirming re-read must never show.
    if (options?.requireLiveAnswer) return resolveOutcome();

    // 036 (issue 234): the deadline itself. If nothing is stored for this URL, there is nothing to
    // fall back to, so the wait simply continues — exactly the behaviour before this file existed,
    // for the one case a deadline cannot improve on.
    return new Promise<T>((resolve, reject) => {
      let settled = false;
      const timer = setTimeout(() => {
        void (async () => {
          if (settled) return;
          const stored = await readStoredView(practitionerId, url).catch(() => null);
          if (settled) return; // resolveOutcome() won the race while storage was being read
          if (!stored || stored.buildId !== BUILD_ID) return; // nothing to fall back to — keep waiting
          settled = true;
          // 036 (issue 234, found in review): read by `outcome`'s own success handler above, so a
          // live answer that arrives after this point is treated as LATE — see that handler's
          // comment for why that must not be the same as an on-time success.
          resolvedFromDeadline = true;
          // The read has not actually failed yet — it simply has not answered inside the budget a
          // screen can wait for one, which is exactly what `error.status === 0` already means below.
          // `resolveOutcome()` still runs to completion in the background (`outcome` is shared, not
          // reconstructed) and corrects this to `'failing'` if the real answer turns out to be a 5xx.
          markShownFromStorage(url, { moment: stored.date, reason: 'unreachable' });
          resolve(stored.body as T);
        })();
      }, deadlineMs);

      resolveOutcome().then(
        (body) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(body);
        },
        (error: unknown) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          reject(error);
        },
      );
    });
  };

  return {
    get,
    post: (url, body, options) => inner.post(url, body, options),
    put: (url, body, options) => inner.put(url, body, options),
    patch: (url, body, options) => inner.patch(url, body, options),
    delete: (url, options) => inner.delete(url, options),
  };
}
